import gsap from "gsap"
import BackArrowL from "images/Icons/BackArrowL.inline.svg"
import ForwardArrowL from "images/Icons/ForwardArrowL.inline.svg"
import UniversalLink from "library/link"
import { css, f, fresponsive, styled } from "library/styled"
import type { Dispatch, SetStateAction } from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import colors from "styles/colors"
import media from "styles/media"
import textStyles from "styles/text"

interface PaginationProps {
	totalElements: number
	setActivePage: Dispatch<SetStateAction<number>>
	activePage: number
	resultsPerPage: number
	scrollTarget: string
	numberOfButtons?: number
}

const addZero = (num: number) => {
	return num < 10 ? `0${num}` : num
}

export default function Pagination({
	totalElements,
	setActivePage,
	activePage,
	resultsPerPage,
	scrollTarget,
	numberOfButtons = 6,
}: PaginationProps) {
	const [currentPage] = useState(1)
	const totalPagesPre = Math.ceil(
		(totalElements - (resultsPerPage - 1)) / resultsPerPage,
	)
	const totalPages =
		totalElements % resultsPerPage === 0 ? totalPagesPre : totalPagesPre + 1

	useEffect(() => {
		setActivePage(currentPage)
	}, [currentPage, setActivePage])

	const handleClick = useCallback(
		(newPage: number | false) => {
			if (newPage) {
				setActivePage(newPage)

				gsap.to(window, {
					duration: 0,
					scrollTo: { y: `#${scrollTarget}`, offsetY: 300 },
				})
			}
		},
		[setActivePage, scrollTarget],
	)

	const pagination = useMemo(() => {
		const renderFirstPageNumbers = (num: number) => {
			const firstPageNumbers = []
			for (let i = 2; i <= num; i++) {
				firstPageNumbers.push(
					<PageNumber
						key={i}
						type="button"
						$active={activePage === i}
						onClick={() => handleClick(i)}
					>
						{addZero(i)}
					</PageNumber>,
				)
			}
			return firstPageNumbers
		}

		const renderMiddleNumbers = () => {
			const minimumMiddle = 2
			const fromButtons = numberOfButtons - 3
			const buttonAmount =
				fromButtons > minimumMiddle ? fromButtons : minimumMiddle

			const pageButtons = []
			for (let i = 0; i < buttonAmount; i++) {
				pageButtons.push(
					<PageNumber
						key={i}
						type="button"
						$active={i === 0}
						onClick={() => handleClick(activePage + i)}
					>
						{addZero(activePage + i)}
					</PageNumber>,
				)
			}
			return pageButtons
		}

		const renderLastNumbers = (Page_Numbers: number) => {
			const pageButtons = []
			for (let i = 0; i < Page_Numbers; i++) {
				const pageNumber = totalPages - Page_Numbers + i
				pageButtons.push(
					<PageNumber
						key={pageNumber}
						type="button"
						$active={activePage === pageNumber}
						onClick={() => handleClick(pageNumber)}
					>
						{addZero(pageNumber)}
					</PageNumber>,
				)
			}
			return pageButtons
		}

		return (
			<>
				<PageNumber
					type="button"
					onClick={() => {
						handleClick(1)
					}}
					$active={activePage === 1}
				>
					01
				</PageNumber>
				{activePage < numberOfButtons && (
					<>
						{renderFirstPageNumbers(numberOfButtons)}
						<PageNumber type="button">..</PageNumber>
					</>
				)}
				{activePage > numberOfButtons - 1 &&
					activePage < totalPages - (numberOfButtons - 2) && (
						<>
							<PageNumber type="button">..</PageNumber>
							{renderMiddleNumbers()}
							<PageNumber type="button">..</PageNumber>
						</>
					)}
				{activePage > numberOfButtons - 1 &&
					activePage > totalPages - (numberOfButtons - 1) && (
						<>
							<PageNumber type="button">..</PageNumber>
							{renderLastNumbers(numberOfButtons - 2)}
						</>
					)}

				<PageNumber
					type="button"
					onClick={() => handleClick(totalPages)}
					$active={activePage === totalPages}
				>
					{addZero(totalPages)}
				</PageNumber>
			</>
		)
	}, [activePage, handleClick, numberOfButtons, totalPages])

	return (
		<Wrapper>
			<ArrowButton
				type="button"
				aria-label="Previous Page"
				disabled={totalPages === 1 || activePage === 1}
				onClick={() => handleClick(activePage > 1 ? activePage - 1 : 1)}
			>
				<BackArrow />
			</ArrowButton>
			<PageNumbers>
				{totalPages <= numberOfButtons + 1
					? Array.from({ length: totalPages }, (_value, i) => {
							const num = i
							return (
								<PageNumber
									type="button"
									key={`page-${num}`}
									onClick={() => handleClick(i + 1)}
									$active={activePage === i + 1}
								>
									{addZero(i + 1)}
								</PageNumber>
							)
						})
					: pagination}
			</PageNumbers>

			<ArrowButton
				type="button"
				aria-label="Next Page"
				disabled={activePage === totalPages}
				onClick={() => handleClick(activePage !== totalPages && activePage + 1)}
			>
				<ForwardArrow />
			</ArrowButton>
		</Wrapper>
	)
}

const PageNumber = styled(
	UniversalLink,
	({ $active }: { $active?: boolean }) => {
		return {
			...f.responsive(css`
				line-height: 100%;
				cursor: pointer;
				display: flex;
				align-items: center;
				justify-content: center;
				text-decoration: none;
				color: ${$active ? colors.green500 : colors.green200};
				border-radius: 100%;
				transition: 0.4s;
				background: transparent;
				${textStyles.titleS};
				border: 0.75px solid ${$active ? colors.green500 : colors.green200};
				width: 24px;
				height: 24px;

				${media.hover} {
					:hover {
						transition: 0.4s;
					}
				}

				:active {
					color: ${colors.green500};
					transition: 0.4s;
				}
			`),

			...f.tablet(css`
				width: 32px;
				height: 32px;
				border-radius: 4px;
			`),

			...f.mobile(css`
				${textStyles.titleXS};
				width: 24px;
				height: 24px;
			`),
		}
	},
)

const Wrapper = styled("div", {
	...f.responsive(css`
		position: relative;
		z-index: 3;
		justify-content: center;
		align-items: center;
		display: flex;
		width: 100%;
		gap: 12px;
	`),
})

const PageNumbers = styled("div", {
	...f.responsive(css`
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 12px;
	`),

	...f.mobile(css`
		gap: 6px;
	`),
})

const ArrowButton = styled("button", {
	...f.responsive(css`
		display: flex;
		align-items: center;
		justify-content: center;
		width: 50px;
		height: 50px;
		border-radius: 100%;
		color: ${colors.green500};
		border: none;
		cursor: pointer;

		rect {
			fill: none;
		}
	`),

	...f.tablet(css`
		width: 40px;
		height: 40px;
	`),

	...f.mobile(css`
		width: 32px;
		height: 32px;
	`),
})

const ForwardArrow = styled(ForwardArrowL, {
	...fresponsive(css`
		color: ${colors.green400};
	`),
})

const BackArrow = styled(BackArrowL, {
	...fresponsive(css`
		color: ${colors.green400};
	`),
})
